import { useEffect, useLayoutEffect, useRef } from "react"
import { useTranslation } from "react-i18next"
import { create } from "zustand"
import { createJSONStorage, persist } from "zustand/middleware"
import { DropdownMenuCheckboxItem, DropdownMenuLabel } from "@/components/ui/dropdown-menu"
import { FaroInsightsWrapper } from "@/grafana-faro"
import { useCurrentLanguage } from "@/hooks/useCurrentLanguage"

export const DARK_APPEARANCE_CLASS_NAME = "dark"
const UI_APPEARANCES = { LIGHT: "light", DARK: "dark" } as const

type UiAppearance = (typeof UI_APPEARANCES)[keyof typeof UI_APPEARANCES]

type IAppearanceStore = { appearance: UiAppearance }

type AppearanceStoreActions = { switchAppearance: (appearance: UiAppearance) => void }

const initialState: IAppearanceStore = { appearance: UI_APPEARANCES.LIGHT }

const useAppearanceStore = create<IAppearanceStore & AppearanceStoreActions>()(
  persist(
    (set) => ({
      ...initialState,
      switchAppearance: (appearance: UiAppearance) => set({ appearance }),
    }),
    { name: "appearanceStore", storage: createJSONStorage(() => localStorage) },
  ),
)

type UiPreferences = { language: string; appearance: UiAppearance }

export const AppearanceSynchronizer = () => {
  const currentLanguage = useCurrentLanguage()
  const appearance = useAppearanceStore((state) => state.appearance)
  const previouslyTrackedUiPreferences = useRef<UiPreferences | null>(null)

  useLayoutEffect(() => {
    document.documentElement.classList.toggle(DARK_APPEARANCE_CLASS_NAME, appearance === UI_APPEARANCES.DARK)
  }, [appearance])

  useEffect(() => {
    const previousUiPreferences = previouslyTrackedUiPreferences.current

    // The ref also absorbs StrictMode's second mount, which repeats the unchanged preferences.
    if (previousUiPreferences?.language === currentLanguage && previousUiPreferences.appearance === appearance) return

    previouslyTrackedUiPreferences.current = { language: currentLanguage, appearance }

    if (!previousUiPreferences) {
      FaroInsightsWrapper.pushEvent("ui_preferences_startup", { language: currentLanguage, appearance })
      return
    }

    const switchedPreference = previousUiPreferences.appearance === appearance ? "language" : "appearance"
    FaroInsightsWrapper.pushEvent("ui_preferences_switched", {
      switchedPreference,
      language: currentLanguage,
      appearance,
    })
  }, [currentLanguage, appearance])

  return null
}

export const AppearanceMenuSection = () => {
  const { t } = useTranslation()
  const appearance = useAppearanceStore((state) => state.appearance)
  const switchAppearance = useAppearanceStore((state) => state.switchAppearance)

  return (
    <>
      <DropdownMenuLabel className="text-on-surface-subdued px-2 py-2.5 text-sm font-medium">
        {t("common.appearance")}
      </DropdownMenuLabel>
      <DropdownMenuCheckboxItem
        data-testid="menu-appearance-light"
        checked={appearance === UI_APPEARANCES.LIGHT}
        onCheckedChange={() => switchAppearance(UI_APPEARANCES.LIGHT)}>
        {t("common.menu.light")}
      </DropdownMenuCheckboxItem>
      <DropdownMenuCheckboxItem
        data-testid="menu-appearance-dark"
        checked={appearance === UI_APPEARANCES.DARK}
        onCheckedChange={() => switchAppearance(UI_APPEARANCES.DARK)}>
        {t("common.menu.dark")}
      </DropdownMenuCheckboxItem>
    </>
  )
}
